import type { PagedResult, SearchFilters } from "src/types/matrimonySearchResults.types";
import { MARITAL_STATUS_OPTIONS } from "src/utils/matrimonyOptions";

export const toUiPage = (page0: number) => page0 + 1;
export const toApiPage = (uiPage1: number) => Math.max(0, uiPage1 - 1);

export const parseBool = (v: string | null): boolean | undefined => {
  if (v == null || v === "") return undefined;
  if (v === "true") return true;
  if (v === "false") return false;
  return undefined;
};

export const parseNum = (v: string | null): number | undefined => {
  if (v == null || v.trim() === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export const formatHeight = (cm?: number) => {
  if (cm == null) return "";
  const totalIn = cm / 2.54;
  const ft = Math.floor(totalIn / 12);
  const inch = Math.round(totalIn % 12);
  return `${ft}′ ${inch}″`;
};

/**
 * Calculate age in years from a date-of-birth.
 * Accepts ISO dates (yyyy-mm-dd) or any string parseable by `Date`.
 */
export const calculateAge = (dateOfBirth?: string): number | undefined => {
  if (!dateOfBirth) return undefined;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return undefined;

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();

  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }

  // Guard against obviously wrong values from bad data.
  if (age < 0 || age > 120) return undefined;
  return age;
};

// Helper to compare filters (excluding page/size) for enabling Apply button, etc.
export const stableFilterKey = (f: SearchFilters) =>
  JSON.stringify({
    gender: f.gender,
    maritalStatus: f.maritalStatus,
    nativePlace: f.nativePlace,
    highestEducation: f.highestEducation,
    employmentSector: f.employmentSector,
    dietaryHabits: f.dietaryHabits,
    smoking: f.smoking,
    drinking: f.drinking,
    minAge: f.minAge,
    maxAge: f.maxAge,
    minHeight: f.minHeight,
    maxHeight: f.maxHeight,
    minIncome: f.minIncome,
    maxIncome: f.maxIncome,
    dosham: f.dosham,
    hasPhoto: f.hasPhoto,
    q: f.q,
  });

// Backend expects enum values exactly: Male | Female | Other
export const toBackendGender = (v?: string) => {
  if (!v) return undefined;
  const norm = String(v).trim().toLowerCase();
  if (norm === "male" || norm === "m") return "Male";
  if (norm === "female" || norm === "f") return "Female";
  if (norm === "other" || norm === "o") return "Other";
  // If UI sends MALE/FEMALE etc, normalize it.
  if (norm === "male" || norm === "female" || norm === "other") return norm[0].toUpperCase() + norm.slice(1);
  return undefined;
};

// We store gender in URL using backend enum values to avoid 400s.
export const toFrontendGender = (v?: string) => {
  if (!v) return undefined;
  return toBackendGender(v);
};

export type MaritalStatusValue = (typeof MARITAL_STATUS_OPTIONS)[number]["value"];

export const toBackendMaritalStatus = (v?: string): MaritalStatusValue | undefined => {
  if (!v) return undefined;
  const norm = String(v).trim();
  const found = MARITAL_STATUS_OPTIONS.find((o) => o.value === norm);
  return found?.value;
};

// Backward-compat: if URL already has legacy strings, normalize them.
export const toFrontendMaritalStatus = (v?: string): MaritalStatusValue | undefined => {
  if (!v) return undefined;
  const raw = String(v).trim();
  const upper = raw.toUpperCase();

  if (upper === "BACHELOR") return "BACHELOR";
  if (upper === "MARRIED") return "MARRIED";
  if (upper === "DIVORCED") return "DIVORCED";
  if (upper === "WIDOWED") return "WIDOWED";

  // Legacy / display strings
  if (upper === "BACHELOR" || upper === "SINGLE") return "BACHELOR";
  if (upper === "NEVER MARRIED" || upper === "NEVER_MARRIED") return "BACHELOR";

  return undefined;
};

export const serializeFiltersToParams = (
  f: SearchFilters,
  opts: {
    toBackendMaritalStatusImpl: (v?: string) => string | undefined;
    toBackendGenderImpl: (v?: string) => string | undefined;
  }
) => {
  const params = new URLSearchParams();
  const put = (k: string, v: any) => {
    if (v === undefined || v === null || v === "") return;
    params.set(k, String(v));
  };

  put("gender", opts.toBackendGenderImpl(f.gender));
  put("maritalStatus", opts.toBackendMaritalStatusImpl(f.maritalStatus));
  put("nativePlace", f.nativePlace);
  put("highestEducation", f.highestEducation);
  put("employmentSector", f.employmentSector);
  put("dietaryHabits", f.dietaryHabits);
  put("smoking", f.smoking);
  put("drinking", f.drinking);
  put("minAge", f.minAge);
  put("maxAge", f.maxAge);
  put("minHeight", f.minHeight);
  put("maxHeight", f.maxHeight);
  put("minIncome", f.minIncome);
  put("maxIncome", f.maxIncome);
  put("dosham", f.dosham);
  put("hasPhoto", f.hasPhoto);
  put("page", f.page);
  put("size", f.size);
  put("q", f.q);

  return params;
};

export const deserializeParamsToFilters = (
  sp: URLSearchParams,
  opts: {
    toFrontendGenderImpl: (v?: string) => string | undefined;
    toFrontendMaritalStatusImpl: (v?: string) => string | undefined;
  }
): SearchFilters => {
  // URL and backend use 0-based pages.
  // Backward-compat: if someone passes a 1-based page (>=1) in the URL, normalize it.
  const rawPage = parseNum(sp.get("page"));
  const page0 = clamp(rawPage == null ? 0 : rawPage >= 1 ? rawPage - 1 : rawPage, 0, 10_000);
  const size = clamp(parseNum(sp.get("size")) ?? 10, 1, 50);

  return {
    gender: opts.toFrontendGenderImpl(sp.get("gender") || undefined),
    maritalStatus: opts.toFrontendMaritalStatusImpl(sp.get("maritalStatus") || undefined),
    nativePlace: sp.get("nativePlace") || undefined,
    highestEducation: sp.get("highestEducation") || undefined,
    employmentSector: sp.get("employmentSector") || undefined,
    dietaryHabits: sp.get("dietaryHabits") || undefined,
    smoking: parseBool(sp.get("smoking")),
    drinking: parseBool(sp.get("drinking")),
    minAge: parseNum(sp.get("minAge")),
    maxAge: parseNum(sp.get("maxAge")),
    minHeight: parseNum(sp.get("minHeight")),
    maxHeight: parseNum(sp.get("maxHeight")),
    minIncome: parseNum(sp.get("minIncome")),
    maxIncome: parseNum(sp.get("maxIncome")),
    dosham: sp.get("dosham") || undefined,
    hasPhoto: parseBool(sp.get("hasPhoto")),
    page: page0,
    size,
    q: sp.get("q") || undefined,
  };
};

export const getResults = <T,>(res: PagedResult<T>): T[] => {
  return (res.content || res.results || res.data || []) as T[];
};

export const getTotal = (res: PagedResult<any>) => {
  return Number(res.totalElements ?? res.total ?? 0);
};
